import { z } from "zod";

/**
 * The cabinet's office and the administration's cabinet doors, **one contract for the doors and for every client**
 * (`GENERIC-DOOR-SWEEP`, Arc 4, A48): `GET /cabinet/me`, `PUT /cabinet/capacities/{dayId}`, `GET /cabinet/fees`,
 * `GET /cabinet/reports`, `POST /expertise/cabinets`, `PUT /expertise/cabinets/{id}/terms`, `GET /expertise/cabinets`,
 * `GET /expertise/thursdays` and `GET /expertise/thursdays/{id}/composition`.
 *
 * Until this file the controllers answered `unknown` (the services under them were already typed, the controller widened
 * them) and expert-web and console-web narrowed by hand. **The cabinet's honoraires and the report's estimated value are
 * whole francs** (`INTEGER` columns): every `*Amount` is `z.number().int()`, so a float or a string where a price stands
 * fails the integration spec that parses the real answer. The report ENTRY and the report itself (the checklist form and
 * its body) are not here: they belong to the checklist round.
 */

/** « Mon cabinet »: the fiche as the cabinet reads it, nothing of Hakili's margin. */
export const cabinetCardSchema = z.strictObject({
  name: z.string(),
  accreditation: z.string().nullable(),
  zoneLabel: z.string().nullable(),
  phone: z.string().nullable(),
  /** The day the cabinet was onboarded, `YYYY-MM-DD`. */
  since: z.string(),
  minimumCapacity: z.number().int(),
  /** The cabinet's honoraires in whole FCFA; `null` until the administration enters it. */
  feeAmount: z.number().int().nullable(),
  active: z.boolean(),
});

/** One coming Thursday: what the cabinet declared, its contractual minimum, what Hakili confirmed on it. */
export const thursdayCapacitySchema = z.strictObject({
  expertiseDayId: z.string(),
  label: z.string(),
  /** `YYYY-MM-DD`. */
  dayOn: z.string(),
  /** `null` while undeclared: the minimum then stands. Zero is a declaration. */
  declared: z.number().int().nullable(),
  declaredAt: z.string().nullable(),
  minimum: z.number().int(),
  effective: z.number().int(),
  confirmed: z.number().int(),
});

export const cabinetMeAnswerSchema = z.strictObject({
  cabinet: cabinetCardSchema,
  capacities: z.array(thursdayCapacitySchema),
});

export const capacityDeclaredAnswerSchema = z.strictObject({ thursday: thursdayCapacitySchema });

/** One line of « Honoraires »: a filed report and the cabinet's fee for it. */
export const feeLineSchema = z.strictObject({
  reportId: z.string(),
  demandReference: z.string(),
  vehicleLabel: z.string(),
  sellerName: z.string(),
  dayLabel: z.string().nullable(),
  reportedAt: z.string(),
  feeAmount: z.number().int().nullable(),
});

export const cabinetFeesAnswerSchema = z.strictObject({
  lines: z.array(feeLineSchema),
  feeAmount: z.number().int().nullable(),
  prevented: z.number().int(),
});

/** One line of « Mes rapports »: the history, with the ninety-day validity following the vehicle. */
export const historyLineSchema = z.strictObject({
  reportId: z.string(),
  demandReference: z.string(),
  vehicleLabel: z.string(),
  vehiclePlate: z.string().nullable(),
  sellerName: z.string(),
  clientName: z.string().nullable(),
  verdict: z.string(),
  points: z.number().int(),
  totalPoints: z.number().int(),
  /** The expert's estimate in whole FCFA. */
  estimatedValueAmount: z.number().int(),
  reportedAt: z.string(),
  validUntil: z.string(),
  entryMode: z.string(),
  visaByName: z.string().nullable(),
  demandId: z.string(),
  version: z.number().int(),
  documentId: z.string().nullable(),
  documentSha256: z.string().nullable(),
  derivedSheetDocumentId: z.string().nullable(),
  supersededVersions: z.number().int(),
  correctionOpen: z.boolean(),
  correctionRequested: z.strictObject({ reason: z.string(), requestedAt: z.string() }).nullable(),
});

export const cabinetHistoryAnswerSchema = z.strictObject({ reports: z.array(historyLineSchema) });

/** `POST /expertise/cabinets`: the cabinet that entered the scheduling pool. */
export const onboardedCabinetAnswerSchema = z.strictObject({
  cabinet: z.strictObject({ id: z.string(), name: z.string(), active: z.boolean() }),
});

/** `PUT /expertise/cabinets/{id}/terms`: the contract terms as they now stand. */
export const cabinetTermsAnswerSchema = z.strictObject({
  cabinet: z.strictObject({
    id: z.string(),
    feeAmount: z.number().int().nullable(),
    minimumCapacity: z.number().int(),
  }),
});

/** One cabinet of the scheduling pool. */
export const poolCabinetSchema = z.strictObject({
  id: z.string(),
  organisationId: z.string(),
  name: z.string(),
  contactName: z.string(),
  phone: z.string(),
  zoneLabel: z.string(),
  feesLabel: z.string(),
  defaultDailyCapacity: z.number().int(),
  /** The cabinet's honoraires in whole FCFA (`OD-29`); `null` until the administration enters it. */
  feeAmount: z.number().int().nullable(),
  active: z.boolean(),
  loadWeek: z.number().int(),
  loadMonth: z.number().int(),
});

export const cabinetPoolAnswerSchema = z.strictObject({ cabinets: z.array(poolCabinetSchema) });

/** The control days: rows, the cadence is data. */
export const controlDaySchema = z.strictObject({
  id: z.string(),
  label: z.string(),
  /** `YYYY-MM-DD`. */
  dayOn: z.string(),
  active: z.boolean(),
});

export const controlDaysAnswerSchema = z.strictObject({ days: z.array(controlDaySchema) });

/** One cabinet's room on one Thursday, as the console's composition reads it. */
export const dayCompositionCabinetSchema = z.strictObject({
  cabinetId: z.string(),
  cabinetName: z.string(),
  capacity: z.number().int(),
  placed: z.number().int(),
  remaining: z.number().int(),
  declared: z.number().int().nullable(),
  minimum: z.number().int(),
  belowMinimum: z.boolean(),
  overflow: z.number().int(),
});

export const dayCompositionAnswerSchema = z.strictObject({
  cabinets: z.array(dayCompositionCabinetSchema),
  total: z.strictObject({ capacity: z.number().int(), placed: z.number().int() }),
});

export type CabinetCard = z.infer<typeof cabinetCardSchema>;
export type ThursdayCapacity = z.infer<typeof thursdayCapacitySchema>;
export type CabinetMeAnswer = z.infer<typeof cabinetMeAnswerSchema>;
export type CapacityDeclaredAnswer = z.infer<typeof capacityDeclaredAnswerSchema>;
export type FeeLine = z.infer<typeof feeLineSchema>;
export type CabinetFeesAnswer = z.infer<typeof cabinetFeesAnswerSchema>;
export type HistoryLine = z.infer<typeof historyLineSchema>;
export type CabinetHistoryAnswer = z.infer<typeof cabinetHistoryAnswerSchema>;
export type OnboardedCabinetAnswer = z.infer<typeof onboardedCabinetAnswerSchema>;
export type CabinetTermsAnswer = z.infer<typeof cabinetTermsAnswerSchema>;
export type PoolCabinet = z.infer<typeof poolCabinetSchema>;
export type CabinetPoolAnswer = z.infer<typeof cabinetPoolAnswerSchema>;
export type ControlDay = z.infer<typeof controlDaySchema>;
export type ControlDaysAnswer = z.infer<typeof controlDaysAnswerSchema>;
export type DayCompositionCabinet = z.infer<typeof dayCompositionCabinetSchema>;
export type DayCompositionAnswer = z.infer<typeof dayCompositionAnswerSchema>;
